'use client';

/**
 * V2-side banner for grids that are the destination of a V1 → V2
 * migration.
 *
 * Why this lives on V2 (not just on V1):
 *
 *   - V1 deliberately stays terse — its banner is a one-liner that
 *     just tells the holder what's happening. The actual mechanics
 *     ("yield is still on V1, claims open at rescue", "abandon V1 to
 *     reclaim deposit post-rescue") only matter once the user is
 *     looking at the V2 board, so we explain them here.
 *   - The banner does NOT render on hvTAKEOVER or any other V2 grid
 *     that happens to use a `WhitelistGovernanceModule`. The
 *     migration / non-migration distinction is decided on classic via
 *     `pool_metadata` (admin toggle), not by the onchain governance
 *     module presence — same module, different UX intent.
 *
 * Pre-asset vs post-asset is gated on the V1 pool's
 * `treasuryReclaimedAt` (the indexer-mirrored onchain timestamp the
 * V1 TSFM `TreasuryReclaimed` event writes). That's the only signal
 * tied to the actual NFT movement; the admin-set `untilMs` /
 * `rewardsMigrated` time gate is kept as a backward-compat fallback
 * for the deploy window when classic hasn't yet shipped the new
 * payload field. See `isMigrationAssetMoved` in `migration.ts`.
 *
 * Visual language follows the rest of the lobby's board-state banners
 * (`BoardInitializingBanner`, `BoardBannedBanner`, `PendingMetadataBanner`):
 * compact, backdrop-blurred, amber-toned (warning, not error), with
 * an uppercase mono header / sentence-case body / full-width footer
 * CTA. The CTA copy + destination flips with pre/post-asset.
 */

import { useEffect, useState } from 'react';
import { ArrowRightLeft, ExternalLink } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  isMigrationAssetMoved,
  type V2MigrationContext,
} from '@/lib/api/fetchers/migration';

export interface V2MigrationBoardBannerProps {
  context: V2MigrationContext;
  className?: string;
}

function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function V2MigrationBoardBanner({ context, className }: V2MigrationBoardBannerProps) {
  const { v1CoinUrl, v1CoinSymbol, untilMs } = context;

  // We hydrate `now` from `Date.now()` on mount rather than during
  // render so the SSR markup and the first client paint match — Next
  // would otherwise warn about hydration mismatch on a render that
  // happens to straddle a second boundary. The 30s tick is just to
  // flip pre-asset → post-asset if the user leaves the tab open
  // through the time-gate fallback boundary; we don't render any
  // countdown.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Only tick when the asset still hasn't moved AND we're relying
    // on the time-gate fallback (no onchain timestamp yet). In every
    // other state the answer is already final.
    if (typeof context.v1TreasuryReclaimedAt === 'number') return;
    if (context.rewardsMigrated) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [context.v1TreasuryReclaimedAt, context.rewardsMigrated]);

  const assetMoved = isMigrationAssetMoved(context, now);

  const eyebrow = assetMoved ? 'Migrated from V1' : 'Migrating from V1';
  const ctaLabel = assetMoved ? 'Abandon V1 seat' : 'View V1 grid';

  return (
    <div
      className={cn(
        'rounded-[6px] border border-amber-500/40 bg-amber-500/10 backdrop-blur-md p-3 text-[11px] text-amber-50 space-y-2',
        className,
      )}
      role="status"
      data-testid="v2-migration-board-banner"
    >
      <div className="flex items-start gap-2">
        <ArrowRightLeft className="h-4 w-4 mt-0.5 shrink-0 text-amber-200" />
        <div className="space-y-1 min-w-0">
          <p className="font-mono uppercase tracking-[0.2em] text-amber-200 text-[9px]">
            {eyebrow} — {v1CoinSymbol}
          </p>
          <p className="text-amber-50/90">
            {assetMoved ? (
              <>
                Rewards now flow on this board. If you still hold a V1 seat,
                abandon it on V1 to reclaim your deposit.
              </>
            ) : (
              <>
                Yield is still flowing on V1. Reservations are seeded but
                claims open here once the rescue lands —{' '}
                <span className="font-semibold text-amber-50">
                  expected {formatTimestamp(untilMs)}
                </span>
                . Sit tight on V1 until then.
              </>
            )}
          </p>
        </div>
      </div>
      <a
        href={v1CoinUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex w-full items-center justify-center gap-1.5 py-1.5 rounded-[3px] bg-amber-500/30 border border-amber-400/50 text-[10px] font-mono uppercase tracking-wide text-amber-50 hover:bg-amber-500/40 transition-colors"
      >
        {ctaLabel}
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}
